/**
 * Floating Navbar runtime
 * - Smart hide/persistent behavior
 * - Home icon actions (native drawers + links)
 * - Collection filter/sort + mirrored pagination
 * - Product add to bag / buy now actions
 */

(function() {
  'use strict';

  if (window.UntamedFloatingNavbarManager) return;

  const OVERLAY_BODY_CLASSES = [
    'drawer-open',
    'newsletter-popup-open',
    'cart-drawer-open',
    'wishlist-drawer-open',
    'menu-drawer-open',
    'search-drawer-open',
    'localization-drawer-open'
  ];

  // Storefront root (works with /en-us style locale prefixes)
  function shopRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  // Last variant announced by the product page's variant picker (product.js)
  let lastAnnouncedVariant = null;
  window.addEventListener('variant:changed', (e) => {
    if (e && e.detail && e.detail.variant) lastAnnouncedVariant = e.detail.variant;
  });

  class FloatingNavbarInstance {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId || '';
      this.mode = root.dataset.mode || 'home';
      this.layoutMode = root.dataset.layoutMode || 'floating';
      this.scrollBehavior = root.dataset.scrollBehavior || 'smart_hide';
      this.enablePagination = root.dataset.enablePagination === 'true';
      this.lastScrollY = window.scrollY || 0;
      this.isHidden = false;
      this.destroyed = false;

      this.paginationWrap = root.querySelector('[data-floating-pagination]');
      this.paginationPages = root.querySelector('[data-pagination-pages]');
      this.paginationPrevBtn = root.querySelector('[data-pagination-prev]');
      this.paginationNextBtn = root.querySelector('[data-pagination-next]');
      this.collectionRoot = root.querySelector('[data-floating-navbar-collection]');
      this.productWishlistBtn = root.querySelector('[data-floating-wishlist-btn]');

      this.handleClick = this.handleClick.bind(this);
      this.handleScroll = this.handleScroll.bind(this);
      this.handleBodyMutation = this.handleBodyMutation.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.handleIntersectionHide = this.handleIntersectionHide.bind(this);
      this.handleWishlistUpdated = this.handleWishlistUpdated.bind(this);

      this.intersectionObserver = null;
      this.bodyObserver = null;
      this._lastOverlayState = false;
    }

    init() {
      if (this.destroyed) return;

      this.root.addEventListener('click', this.handleClick);
      window.addEventListener('resize', this.handleResize);
      this.bindVisibilityEngine();
      this.initCollectionPagination();
      this.bindWishlistState();
      this.initIcons();

      // Pre-load product data so the first Add to Bag tap is instant
      if (this.mode === 'product') this.getProductJson();
    }

    destroy() {
      if (this.destroyed) return;
      this.destroyed = true;

      this.root.removeEventListener('click', this.handleClick);
      window.removeEventListener('resize', this.handleResize);
      window.removeEventListener('scroll', this.handleScroll);
      document.removeEventListener('wishlist:updated', this.handleWishlistUpdated);
      window.removeEventListener('storage', this.handleWishlistUpdated);

      if (this.bodyObserver) {
        this.bodyObserver.disconnect();
        this.bodyObserver = null;
      }

      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
        this.intersectionObserver = null;
      }
    }

    initIcons() {
      if (window.UntamedIcons && typeof window.UntamedIcons.refresh === 'function') {
        window.UntamedIcons.refresh();
      }
    }

    bindVisibilityEngine() {
      if (this.scrollBehavior === 'smart_hide') {
        window.addEventListener('scroll', this.handleScroll, { passive: true });
      }

      this.bodyObserver = new MutationObserver(this.handleBodyMutation);
      this.bodyObserver.observe(document.body, {
        attributes: true,
        attributeFilter: ['class', 'style']
      });

      this.setupProductBarOverlapGuard();
      this.applyVisibility();
    }

    setupProductBarOverlapGuard() {
      const productBar = document.querySelector('.product-floating-bar');
      if (!productBar) return;

      this.intersectionObserver = new MutationObserver(this.handleIntersectionHide);
      this.intersectionObserver.observe(productBar, {
        attributes: true,
        attributeFilter: ['class', 'style']
      });
    }

    handleIntersectionHide() {
      this.applyVisibility();
    }

    handleResize() {
      this.applyVisibility();
      if (this.mode === 'collection' && this.enablePagination) {
        this.initCollectionPagination();
      }
    }

    handleBodyMutation() {
      const currentOverlayState = this.isAnyOverlayOpen();
      if (currentOverlayState === this._lastOverlayState) return;
      this._lastOverlayState = currentOverlayState;
      this.applyVisibility();
    }

    isAnyOverlayOpen() {
      if (document.body.style.overflow === 'hidden') return true;

      for (let i = 0; i < OVERLAY_BODY_CLASSES.length; i += 1) {
        if (document.body.classList.contains(OVERLAY_BODY_CLASSES[i])) {
          return true;
        }
      }

      const productFloatingBarVisible = document.querySelector('.product-floating-bar.is-visible');
      if (productFloatingBarVisible) return true;

      return false;
    }

    applyVisibility() {
      if (this.isAnyOverlayOpen()) {
        this.hide();
        return;
      }

      if (this.scrollBehavior !== 'smart_hide') {
        this.show();
        return;
      }

      const currentY = window.scrollY || 0;
      if (currentY <= 10) {
        this.show();
      }
    }

    handleScroll() {
      if (this.scrollBehavior !== 'smart_hide') return;

      if (this._scrollRaf) return;
      this._scrollRaf = requestAnimationFrame(() => {
        this._scrollRaf = null;
        this._processScroll();
      });
    }

    _processScroll() {
      if (this.isAnyOverlayOpen()) {
        this.hide();
        return;
      }

      const currentY = window.scrollY || 0;
      const delta = currentY - this.lastScrollY;

      if (currentY <= 10) {
        this.show();
      } else if (delta > 20) {
        this.hide();
      } else if (delta < -20) {
        this.show();
      } else {
        return;
      }

      this.lastScrollY = currentY;
    }

    hide() {
      if (this.isHidden) return;
      this.isHidden = true;
      this.root.classList.add('is-hidden');
    }

    show() {
      if (!this.isHidden) return;
      this.isHidden = false;
      this.root.classList.remove('is-hidden');
    }

    handleClick(event) {
      const navActionEl = event.target.closest('[data-nav-action]');
      if (navActionEl && this.root.contains(navActionEl)) {
        this.handleNavAction(event, navActionEl);
        return;
      }

      const productActionEl = event.target.closest('[data-product-action]');
      if (productActionEl && this.root.contains(productActionEl)) {
        this.handleProductAction(event, productActionEl);
        return;
      }

      const prevBtn = event.target.closest('[data-pagination-prev]');
      if (prevBtn && this.root.contains(prevBtn)) {
        event.preventDefault();
        this.navigateTo(prevBtn.dataset.href);
        return;
      }

      const nextBtn = event.target.closest('[data-pagination-next]');
      if (nextBtn && this.root.contains(nextBtn)) {
        event.preventDefault();
        this.navigateTo(nextBtn.dataset.href);
        return;
      }

      const pagePill = event.target.closest('[data-pagination-url]');
      if (pagePill && this.root.contains(pagePill)) {
        event.preventDefault();
        this.navigateTo(pagePill.dataset.paginationUrl);
      }
    }

    handleNavAction(event, element) {
      const action = element.dataset.navAction;
      const fallbackLink = element.dataset.linkUrl || '';
      if (!action) return;

      if (action === 'home_link' || action === 'custom_link') {
        this.activateHomeIcon(element);
        return;
      }

      event.preventDefault();
      this.activateHomeIcon(element);

      switch (action) {
        case 'search_drawer':
          window.dispatchEvent(new CustomEvent('search:open-drawer'));
          break;
        case 'cart_drawer':
          window.dispatchEvent(new CustomEvent('cart:open'));
          break;
        case 'wishlist_drawer': {
          const productId = this.getWishlistProductId();
          const inWishlistBefore = productId ? this.isProductInWishlist(productId) : null;

          let toggledViaApi = false;
          let toggledViaProxyBtn = false;
          let toggleResult = null;

          if (this.mode === 'product' && productId) {
            const wishlistPayload = this.buildWishlistPayload(productId);
            if (wishlistPayload && window.Wishlist && typeof window.Wishlist.toggle === 'function') {
              try {
                toggleResult = window.Wishlist.toggle(wishlistPayload);
                toggledViaApi = true;
              } catch (error) {
                // noop
              }
            }

            if (!toggledViaApi) {
              const nativeWishlistBtn = this.getNativeWishlistButton(productId);
              if (nativeWishlistBtn && typeof nativeWishlistBtn.click === 'function') {
                nativeWishlistBtn.click();
                toggledViaProxyBtn = true;
              }
            }

            this.syncWishlistState();
          }

          window.dispatchEvent(new CustomEvent('wishlist:open'));
          break;
        }
        case 'menu_drawer':
          if (!this.clickFirst('[data-drawer-toggle]') && fallbackLink) {
            this.navigateTo(fallbackLink);
          }
          break;
        case 'localization_drawer':
          if (!this.clickFirst('[data-localization-toggle]') && fallbackLink) {
            this.navigateTo(fallbackLink);
          }
          break;
        case 'collection_filter':
          if (!this.clickFirst('#filter-toggle') && fallbackLink) {
            this.navigateTo(fallbackLink);
          }
          break;
        case 'collection_sort':
          if (!this.openCollectionSort() && fallbackLink) {
            this.navigateTo(fallbackLink);
          }
          break;
        default:
          if (fallbackLink) {
            this.navigateTo(fallbackLink);
          }
      }
    }

    handleProductAction(event, element) {
      event.preventDefault();

      const action = element.dataset.productAction;
      if (action === 'add_to_bag') {
        this.handleAddToBag(element);
      } else if (action === 'buy_now') {
        this.handleBuyNow(element);
      }
    }

    bindWishlistState() {
      if (this.mode !== 'product' || !this.productWishlistBtn) return;
      document.addEventListener('wishlist:updated', this.handleWishlistUpdated);
      window.addEventListener('storage', this.handleWishlistUpdated);
      this.syncWishlistState();
    }

    handleWishlistUpdated(event) {
      if (event && event.type === 'storage' && event.key && event.key !== 'untamed_wishlist') {
        return;
      }
      this.syncWishlistState();
    }

    syncWishlistState() {
      if (!this.productWishlistBtn) return;
      const productId = this.getWishlistProductId();
      if (!productId) {
        this.productWishlistBtn.classList.remove('is-in-wishlist');
        return;
      }
      const inWishlist = this.isProductInWishlist(productId);
      this.productWishlistBtn.classList.toggle('is-in-wishlist', inWishlist);
    }

    getWishlistProductId() {
      if (!this.productWishlistBtn) return null;
      const productId = parseInt(this.productWishlistBtn.dataset.productId || '', 10);
      return Number.isFinite(productId) && productId > 0 ? productId : null;
    }

    isProductInWishlist(productId) {
      if (!productId) return false;

      if (window.Wishlist && typeof window.Wishlist.has === 'function') {
        try {
          return Boolean(window.Wishlist.has(productId));
        } catch (error) {
          // Fall through to localStorage fallback.
        }
      }

      try {
        const raw = window.localStorage ? window.localStorage.getItem('untamed_wishlist') : null;
        if (!raw) return false;
        const items = JSON.parse(raw);
        if (!Array.isArray(items)) return false;
        return items.some((item) => parseInt(item && item.id, 10) === productId);
      } catch (error) {
        return false;
      }
    }

    getNativeWishlistButton(productId) {
      if (!productId) return null;
      const exactBtn = document.querySelector(`[data-wishlist-btn][data-product-id="${productId}"]`);
      if (exactBtn) return exactBtn;
      const anyBtn = document.querySelector('[data-wishlist-btn]');
      return anyBtn || null;
    }

    getProductHandleFromPath() {
      const match = window.location.pathname.match(/\/products\/([^/?#]+)/);
      if (!match || !match[1]) return '';
      try {
        return decodeURIComponent(match[1]);
      } catch (error) {
        return match[1];
      }
    }

    buildWishlistPayload(productId) {
      if (!productId) return null;

      const sourceBtn = this.getNativeWishlistButton(productId);
      const variantId = this.getCurrentVariantId();

      const parseCents = (raw) => {
        const numeric = parseInt(raw || '', 10);
        return Number.isFinite(numeric) ? numeric : 0;
      };

      const handle = (sourceBtn && sourceBtn.dataset && sourceBtn.dataset.productHandle) || this.getProductHandleFromPath();
      const title = (sourceBtn && sourceBtn.dataset && sourceBtn.dataset.productTitle) ||
        (document.querySelector('h1') ? document.querySelector('h1').textContent.trim() : document.title || '');
      const image = (sourceBtn && sourceBtn.dataset && sourceBtn.dataset.productImage) ||
        (document.querySelector('meta[property="og:image"]') ? document.querySelector('meta[property="og:image"]').getAttribute('content') : '');
      const priceFromDataset = sourceBtn && sourceBtn.dataset ? sourceBtn.dataset.productPrice : '';
      const compareFromDataset = sourceBtn && sourceBtn.dataset ? sourceBtn.dataset.productCompareAtPrice : '';
      const price = parseCents(priceFromDataset);
      const compareAtPriceParsed = parseCents(compareFromDataset);
      const compareAtPrice = compareAtPriceParsed > 0 ? compareAtPriceParsed : null;
      const url = (sourceBtn && sourceBtn.dataset && sourceBtn.dataset.productUrl) || window.location.pathname;

      if (!handle) return null;

      return {
        id: productId,
        handle: handle,
        title: title,
        image: image,
        price: price,
        compareAtPrice: compareAtPrice,
        url: url,
        variantId: (sourceBtn && sourceBtn.dataset && sourceBtn.dataset.variantId) || variantId || null
      };
    }

    getFixedHeaderOffset() {
      const raw = window.getComputedStyle(document.documentElement).getPropertyValue('--untamed-fixed-header-offset').trim();
      const parsed = parseInt(raw || '0', 10);
      return Number.isFinite(parsed) ? parsed : 0;
    }

    activateHomeIcon(element) {
      return element;
    }

    initCollectionPagination() {
      if (this.mode !== 'collection' || !this.collectionRoot) {
        return;
      }

      if (!this.enablePagination || !this.paginationWrap || !this.paginationPages) {
        this.setCollectionPaginationState(false);
        if (this.paginationWrap) this.paginationWrap.hidden = true;
        return;
      }

      const sourceNav = document.querySelector('.collection-pagination');
      if (!sourceNav) {
        this.paginationWrap.hidden = true;
        this.setCollectionPaginationState(false);
        return;
      }

      const prevLink = sourceNav.querySelector('a[aria-label="Previous page"]');
      const nextLink = sourceNav.querySelector('a[aria-label="Next page"], .collection-pagination__item--next');
      const pageParts = Array.from(sourceNav.querySelectorAll('.collection-pagination__item'))
        .filter((node) => /^\d+$/.test((node.textContent || '').trim()));

      this.paginationPages.innerHTML = '';
      if (!pageParts.length) {
        this.paginationWrap.hidden = true;
        this.setCollectionPaginationState(false);
        return;
      }

      pageParts.forEach((part) => {
        const title = (part.textContent || '').trim();
        const isActive = part.classList.contains('is-active') || part.getAttribute('aria-current') === 'page';
        const isLink = part.tagName.toLowerCase() === 'a';
        const href = isLink ? part.getAttribute('href') : '';

        const tagName = isLink ? 'a' : 'span';
        const pill = document.createElement(tagName);
        pill.className = 'floating-navbar__pagination-pill';
        if (isActive) pill.classList.add('is-active');
        pill.textContent = title;

        if (isLink && href) {
          pill.setAttribute('href', href);
          pill.dataset.paginationUrl = href;
        } else {
          pill.setAttribute('aria-current', 'page');
        }

        this.paginationPages.appendChild(pill);
      });

      if (this.paginationPrevBtn) {
        if (prevLink && prevLink.getAttribute('href')) {
          this.paginationPrevBtn.dataset.href = prevLink.getAttribute('href');
          this.paginationPrevBtn.disabled = false;
        } else {
          this.paginationPrevBtn.dataset.href = '';
          this.paginationPrevBtn.disabled = true;
        }
      }

      if (this.paginationNextBtn) {
        if (nextLink && nextLink.getAttribute('href')) {
          this.paginationNextBtn.dataset.href = nextLink.getAttribute('href');
          this.paginationNextBtn.disabled = false;
        } else {
          this.paginationNextBtn.dataset.href = '';
          this.paginationNextBtn.disabled = true;
        }
      }

      this.paginationWrap.hidden = false;
      this.setCollectionPaginationState(true);
      this.initIcons();
    }

    setCollectionPaginationState(active) {
      if (!this.collectionRoot) return;
      this.collectionRoot.classList.toggle('is-pagination-active', Boolean(active));
    }

    openCollectionSort() {
      const sortWrapper = this.getCollectionSortWrapper();
      if (!sortWrapper) {
        return false;
      }

      if (!sortWrapper._selectComponent) {
        if (typeof window.SelectComponent !== 'function') {
          return false;
        }
        sortWrapper._selectComponent = new window.SelectComponent(sortWrapper);
      }

      if (sortWrapper._selectComponent && typeof sortWrapper._selectComponent.open === 'function') {
        const fixedHeaderOffset = this.getFixedHeaderOffset();
        const wrapperRectBefore = sortWrapper.getBoundingClientRect();
        const needsScroll = wrapperRectBefore.top < fixedHeaderOffset || wrapperRectBefore.bottom > window.innerHeight;
        if (needsScroll) {
          const targetY = Math.max(0, window.scrollY + wrapperRectBefore.top - fixedHeaderOffset - 12);
          window.scrollTo({ top: targetY, behavior: 'smooth' });
        }

        requestAnimationFrame(() => {
          if (sortWrapper._selectComponent && typeof sortWrapper._selectComponent.open === 'function') {
            sortWrapper._selectComponent.open();
          }
        });
        return true;
      }

      return false;
    }

    getCollectionSortWrapper() {
      const wrapperCandidates = Array.from(document.querySelectorAll('.efs-toolbar__sort [data-select]'));
      const visibleWrapper = wrapperCandidates.find((el) => this.isElementVisible(el));
      if (visibleWrapper) return visibleWrapper;
      if (wrapperCandidates.length) return wrapperCandidates[0];

      const nativeCandidates = Array.from(document.querySelectorAll('#sort-select'));
      const visibleNative = nativeCandidates.find((el) => this.isElementVisible(el)) || nativeCandidates[0];
      return visibleNative ? visibleNative.closest('[data-select]') : null;
    }

    isElementVisible(element) {
      if (!element) return false;
      if (element.hidden) return false;
      const style = window.getComputedStyle(element);
      if (style.display === 'none' || style.visibility === 'hidden') return false;
      return element.offsetParent !== null || style.position === 'fixed';
    }

    clickFirst(selector) {
      const el = document.querySelector(selector);
      if (el && typeof el.click === 'function') {
        el.click();
        return true;
      }
      return false;
    }

    navigateTo(url) {
      if (!url || url === '#') return;
      window.location.href = url;
    }

    /* ------------------------------------------------------------
     * PRODUCT ACTIONS (Pavão fix)
     * Old code relied on the page's "Add to Cart" block form, which is
     * disabled in the product template — so Add to Bag did nothing.
     * Also, its fallback `form[action="/cart/add"]` could grab a form
     * from another section (product cards) and add the wrong product.
     * Now: read the selected Style + Size from the variant pills and
     * add that exact variant ourselves.
     * ------------------------------------------------------------ */

    getMainProductForm() {
      return document.querySelector(
        '#product-info-container form[data-product-form], #product-media-container form[data-product-form]'
      );
    }

    getProductJson() {
      if (this._productPromise) return this._productPromise;
      const handle = this.getProductHandleFromPath();
      if (!handle) return Promise.resolve(null);
      this._productPromise = fetch(`${shopRoot()}products/${encodeURIComponent(handle)}.js`, {
        headers: { Accept: 'application/json' }
      })
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null);
      return this._productPromise;
    }

    async resolveSelectedVariant() {
      const product = await this.getProductJson();
      if (!product || !Array.isArray(product.variants)) {
        return lastAnnouncedVariant;
      }

      const optionNames = (product.options || []).map((o) => (typeof o === 'string' ? o : o.name));
      const selected = optionNames.map((name) => {
        const safe = window.CSS && CSS.escape ? CSS.escape(name) : name.replace(/"/g, '\\"');
        const btn = document.querySelector(
          `#product-info-container .active[data-option-name="${safe}"], .product-variants-block .active[data-option-name="${safe}"]`
        );
        return btn ? btn.dataset.optionValue : null;
      });

      let variant = null;
      if (selected.length && selected.every(Boolean)) {
        variant = product.variants.find((v) => v.options.every((o, i) => o === selected[i]));
      }
      if (!variant && lastAnnouncedVariant) {
        variant = product.variants.find((v) => v.id === lastAnnouncedVariant.id) || lastAnnouncedVariant;
      }
      if (!variant) {
        const urlId = new URL(window.location.href).searchParams.get('variant');
        if (urlId) variant = product.variants.find((v) => String(v.id) === urlId);
      }
      if (!variant) {
        variant = product.variants.find((v) => v.available) || product.variants[0];
      }
      return variant || null;
    }

    flashLabel(button, text) {
      const label = button && button.querySelector('span');
      if (!label) return;
      if (label.dataset.originalText === undefined) {
        label.dataset.originalText = label.textContent;
      }
      label.textContent = text;
      clearTimeout(button._flashTimer);
      button._flashTimer = setTimeout(() => {
        if (label.dataset.originalText !== undefined) {
          label.textContent = label.dataset.originalText;
          delete label.dataset.originalText;
        }
      }, 1800);
    }

    async addVariantToCart(variantId, openDrawer) {
      const id = parseInt(variantId, 10);
      if (!Number.isFinite(id)) throw new Error('Invalid variant ID.');

      // CartAPI dispatches cart:item-added (opens the drawer) + cart:updated
      if (openDrawer && window.CartAPI && typeof window.CartAPI.addItem === 'function') {
        await window.CartAPI.addItem(id, 1);
        return;
      }

      const response = await fetch(`${shopRoot()}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: id, quantity: 1 })
      });
      const item = await response.json();
      if (!response.ok) {
        throw new Error(item.description || item.message || 'Failed to add to cart.');
      }

      if (openDrawer) {
        const cart = await (await fetch(`${shopRoot()}cart.js`)).json();
        window.dispatchEvent(new CustomEvent('cart:item-added', { detail: { item: item, cart: cart } }));
        window.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
      }
    }

    async handleAddToBag(button) {
      if (!button || button.disabled) return;

      // If the product page's own Add to Cart block is ever re-enabled, use it.
      const mainForm = this.getMainProductForm();
      if (mainForm) {
        if (typeof mainForm.requestSubmit === 'function') {
          mainForm.requestSubmit();
        } else {
          mainForm.submit();
        }
        return;
      }

      this.setActionLoading(button, true);
      try {
        const variant = await this.resolveSelectedVariant();
        if (!variant) throw new Error('No variant found for this product.');
        if (variant.available === false) {
          this.setActionLoading(button, false);
          this.flashLabel(button, 'Sold out');
          return;
        }
        await this.addVariantToCart(variant.id, true);
        this.setActionLoading(button, false, 600);
        this.flashLabel(button, 'Added');
      } catch (error) {
        console.error('Floating navbar add-to-bag failed:', error);
        this.setActionLoading(button, false);
        this.flashLabel(button, 'Try again');
      }
    }

    async handleBuyNow(button) {
      if (!button || button.disabled) return;

      // Only use a Buy-it-now button that belongs to the main product
      const dynamicCheckoutBtn = document.querySelector(
        '#product-info-container .shopify-payment-button__button, #product-media-container .shopify-payment-button__button'
      );
      if (dynamicCheckoutBtn && !dynamicCheckoutBtn.disabled) {
        dynamicCheckoutBtn.click();
        return;
      }

      this.setActionLoading(button, true);
      try {
        const variant = await this.resolveSelectedVariant();
        if (!variant) throw new Error('No variant found for this product.');
        if (variant.available === false) {
          this.setActionLoading(button, false);
          this.flashLabel(button, 'Sold out');
          return;
        }
        await this.addVariantToCart(variant.id, false);
        window.location.href = `${shopRoot()}checkout`;
      } catch (error) {
        console.error('Floating navbar buy-now failed:', error);
        this.setActionLoading(button, false);
        this.flashLabel(button, 'Try again');
      }
    }

    // Synchronous best guess (used by the wishlist payload)
    getCurrentVariantId() {
      const form = this.getMainProductForm();
      if (form) {
        const field = form.querySelector('[name="id"]');
        if (field && field.value) return field.value;
      }

      if (lastAnnouncedVariant && lastAnnouncedVariant.id) return String(lastAnnouncedVariant.id);

      const urlId = new URL(window.location.href).searchParams.get('variant');
      if (urlId) return urlId;

      const scoped = document.querySelector(
        '#product-info-container input[name="id"], #product-info-container select[name="id"]'
      );
      if (scoped && scoped.value) return scoped.value;

      return null;
    }

    setActionLoading(button, loading, releaseDelay) {
      if (!button) return;
      if (loading) {
        button.disabled = true;
        button.classList.add('is-loading');
        return;
      }

      const clear = () => {
        button.disabled = false;
        button.classList.remove('is-loading');
      };

      if (releaseDelay && releaseDelay > 0) {
        window.setTimeout(clear, releaseDelay);
      } else {
        clear();
      }
    }

  }

  const manager = {
    instances: new Map(),

    initIn(container) {
      const scope = container && container.querySelectorAll ? container : document;
      const roots = scope.querySelectorAll('[data-floating-navbar]');

      roots.forEach((root) => {
        const sectionId = root.dataset.sectionId || root.id;
        if (!sectionId || this.instances.has(sectionId)) return;
        const instance = new FloatingNavbarInstance(root);
        instance.init();
        this.instances.set(sectionId, instance);
      });
    },

    destroyIn(container) {
      if (!container || !container.querySelectorAll) return;
      const roots = container.querySelectorAll('[data-floating-navbar]');
      roots.forEach((root) => {
        const sectionId = root.dataset.sectionId || root.id;
        const instance = this.instances.get(sectionId);
        if (!instance) return;
        instance.destroy();
        this.instances.delete(sectionId);
      });
    },

    destroyAll() {
      this.instances.forEach((instance) => instance.destroy());
      this.instances.clear();
    }
  };

  window.UntamedFloatingNavbarManager = manager;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      manager.initIn(document);
    });
  } else {
    manager.initIn(document);
  }

  document.addEventListener('shopify:section:load', function(event) {
    manager.initIn(event.target);
  });

  document.addEventListener('shopify:section:unload', function(event) {
    manager.destroyIn(event.target);
  });
})();